import React, { useState } from "react";
import {
    useGetSalaryPiggyBankUsersQuery,
    useGetSalaryPiggyBankPaymentsQuery,
    useGetSalaryPiggyBankSettingsQuery,
    useUpdateSalaryPiggyBankSettingsMutation,
    useGetSalaryPiggyBankMonthlyStatsQuery,
    useGetSalaryPiggyBankHistoricalStatsQuery,
    useRemoveSalaryPiggyBankUserMutation,
    useRestoreSalaryPiggyBankUserMutation,
} from "../api";
import { SalaryPiggyBankUsersParams, SalaryPiggyBankPaymentsParams } from "../api/salaryPiggyBank";
import { SettingsIcon, UsersIcon, BanknoteIcon } from "lucide-react";
import {
    ResponsiveContainer,
    ComposedChart,
    Bar,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
} from "recharts";

type Tab = "users" | "payments";

const SalaryPiggyBankPage: React.FC = () => {
    const [activeTab, setActiveTab] = useState<Tab>("users");

    // Settings form
    const { data: originalSettings } = useGetSalaryPiggyBankSettingsQuery();
    const [goalAmount, setGoalAmount] = useState<string>("");
    const [minAmount, setMinAmount] = useState<string>("");
    const [settingsSaved, setSettingsSaved] = useState(false);
    const [updateSettings, { isLoading: isSavingSettings }] = useUpdateSalaryPiggyBankSettingsMutation();

    React.useEffect(() => {
        if (originalSettings) {
            setGoalAmount(String(originalSettings.goal_amount));
            setMinAmount(String(originalSettings.min_amount));
        }
    }, [originalSettings]);

    // Monthly stats
    const { data: monthlyStats } = useGetSalaryPiggyBankMonthlyStatsQuery();

    // Historical stats (chart)
    const { data: historicalData } = useGetSalaryPiggyBankHistoricalStatsQuery();

    // Users tab
    const [usersParams, setUsersParams] = useState<SalaryPiggyBankUsersParams>({
        page: 1,
        per_page: 20,
        search: "",
        status: "",
    });
    const { data: usersData, isLoading: usersLoading, refetch: refetchUsers } = useGetSalaryPiggyBankUsersQuery(usersParams);

    // Payments tab
    const [paymentsParams, setPaymentsParams] = useState<SalaryPiggyBankPaymentsParams>({
        page: 1,
        per_page: 20,
        type: "",
        date_from: "",
        date_to: "",
    });
    const { data: paymentsData, isLoading: paymentsLoading } = useGetSalaryPiggyBankPaymentsQuery(paymentsParams);

    // Actions
    const [removeUser] = useRemoveSalaryPiggyBankUserMutation();
    const [restoreUser] = useRestoreSalaryPiggyBankUserMutation();

    const handleSaveSettings = async () => {
        try {
            await updateSettings({
                goal_amount: parseFloat(goalAmount),
                min_amount: parseFloat(minAmount),
            }).unwrap();
            setSettingsSaved(true);
            setTimeout(() => setSettingsSaved(false), 3000);
        } catch (e) {
            alert("Ошибка при сохранении настроек");
        }
    };

    const handleRemoveUser = async (userId: number) => {
        if (!window.confirm("Удалить (кикнуть) этого участника из программы?")) return;
        try {
            await removeUser(userId).unwrap();
            refetchUsers();
        } catch (e) {
            alert("Ошибка удаления");
        }
    };

    const handleRestoreUser = async (userId: number) => {
        if (!window.confirm("Восстановить участника в программе?")) return;
        try {
            await restoreUser(userId).unwrap();
            refetchUsers();
        } catch (e) {
            alert("Ошибка восстановления");
        }
    };

    const formatDate = (d: string) => {
        try { return new Date(d).toLocaleString("ru-RU"); } catch { return d; }
    };

    const renderPagination = (
        currentPage: number,
        lastPage: number,
        onPageChange: (p: number) => void
    ) => {
        if (lastPage <= 1) return null;
        const pages: React.ReactElement[] = [];
        const start = Math.max(1, currentPage - 3);
        const end = Math.min(lastPage, start + 6);
        for (let i = start; i <= end; i++) {
            pages.push(
                <button
                    key={i}
                    onClick={() => onPageChange(i)}
                    className={`px-3 py-1 mx-1 rounded ${
                        i === currentPage
                            ? "bg-blue-600 text-white"
                            : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600"
                    }`}
                >
                    {i}
                </button>
            );
        }
        return (
            <div className="flex justify-center items-center mt-6 space-x-2">
                <button
                    onClick={() => onPageChange(currentPage - 1)}
                    disabled={currentPage <= 1}
                    className="px-3 py-1 rounded bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 disabled:opacity-50"
                >
                    ←
                </button>
                {pages}
                <button
                    onClick={() => onPageChange(currentPage + 1)}
                    disabled={currentPage >= lastPage}
                    className="px-3 py-1 rounded bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 disabled:opacity-50"
                >
                    →
                </button>
                <span className="text-sm text-gray-500 dark:text-gray-400 ml-2">
                    Стр. {currentPage} / {lastPage}
                </span>
            </div>
        );
    };

    return (
        <div className="container mx-auto px-4 py-6">
            {/* Header */}
            <div className="mb-6">
                <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                    🐷 Зарплатная кубышка
                </h1>
                <p className="text-gray-600 dark:text-gray-400 mt-1">
                    Управление программой накопления
                </p>
            </div>

            {/* Monthly Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-5 flex flex-col gap-1">
                    <span className="text-sm text-gray-500 dark:text-gray-400">Оплатили в этом месяце</span>
                    <span className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                        {monthlyStats?.paid_count ?? "—"}
                    </span>
                    <span className="text-xs text-gray-400">участников</span>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-5 flex flex-col gap-1">
                    <span className="text-sm text-gray-500 dark:text-gray-400">Собрано в этом месяце</span>
                    <span className="text-3xl font-bold text-green-600 dark:text-green-400">
                        ${monthlyStats ? Number(monthlyStats.total_collected).toFixed(2) : "—"}
                    </span>
                    <span className="text-xs text-gray-400">суммарно</span>
                </div>
            </div>

            {/* Historical Chart */}
            {historicalData?.data && historicalData.data.length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-6">
                    <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-gray-100">
                        📈 История по месяцам
                    </h2>
                    <ResponsiveContainer width="100%" height={300}>
                        <ComposedChart data={historicalData.data} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(156,163,175,0.3)" />
                            <XAxis
                                dataKey="label"
                                tick={{ fontSize: 12, fill: "#9ca3af" }}
                            />
                            <YAxis
                                yAxisId="amount"
                                orientation="left"
                                tick={{ fontSize: 12, fill: "#9ca3af" }}
                                tickFormatter={(v) => `$${v}`}
                            />
                            <YAxis
                                yAxisId="users"
                                orientation="right"
                                tick={{ fontSize: 12, fill: "#9ca3af" }}
                            />
                            <Tooltip
                                formatter={(value, name) =>
                                    name === "Сумма"
                                        ? [`$${Number(value ?? 0).toFixed(2)}`, name]
                                        : [value ?? 0, name]
                                }
                            />
                            <Legend />
                            <Bar
                                yAxisId="amount"
                                dataKey="total_collected"
                                name="Сумма"
                                fill="#3b82f6"
                                radius={[4, 4, 0, 0]}
                                opacity={0.85}
                            />
                            <Line
                                yAxisId="users"
                                type="monotone"
                                dataKey="total_users"
                                name="Участников"
                                stroke="#10b981"
                                strokeWidth={2}
                                dot={{ r: 4, fill: "#10b981" }}
                            />
                        </ComposedChart>
                    </ResponsiveContainer>
                </div>
            )}

            {/* Settings Block */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-6">
                <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-gray-100">
                    <SettingsIcon className="inline mr-2" /> Настройки программы
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Конечная сумма сбора в месяц (salary_piggy_goal_amount)
                        </label>
                        <input
                            type="number"
                            value={goalAmount}
                            onChange={(e) => setGoalAmount(e.target.value)}
                            className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                            min="1"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Минимальная сумма автооплаты (salary_piggy_min_amount)
                        </label>
                        <input
                            type="number"
                            value={minAmount}
                            onChange={(e) => setMinAmount(e.target.value)}
                            className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                            min="1"
                        />
                    </div>
                </div>
                <div className="mt-4 flex items-center gap-3">
                    <button
                        onClick={handleSaveSettings}
                        disabled={isSavingSettings}
                        className="px-5 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                    >
                        {isSavingSettings ? "Сохранение..." : "Сохранить"}
                    </button>
                    {settingsSaved && (
                        <span className="text-green-600 dark:text-green-400 text-sm font-medium">
                            ✓ Настройки сохранены
                        </span>
                    )}
                </div>
            </div>

            {/* Tabs */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
                <div className="flex border-b border-gray-200 dark:border-gray-700">
                    {(["users", "payments"] as Tab[]).map((tab) => (
                        <button
                            key={tab}
                            onClick={() => setActiveTab(tab)}
                            className={`px-6 py-3 text-sm font-medium transition-colors ${
                                activeTab === tab
                                    ? "border-b-2 border-blue-600 text-blue-600 dark:text-blue-400"
                                    : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
                            }`}
                        >
                            {tab === "users" ? <UsersIcon className="inline mr-2" /> : <BanknoteIcon className="inline mr-2" />}
                            {tab === "users" ? "Участники" : "Платежи"}
                        </button>
                    ))}
                </div>

                {/* Users Tab */}
                {activeTab === "users" && (
                    <div>
                        {/* Users Filters */}
                        <div className="p-4 border-b border-gray-100 dark:border-gray-700 grid grid-cols-1 md:grid-cols-3 gap-3">
                            <input
                                type="text"
                                placeholder="Поиск по имени, ID, email..."
                                value={usersParams.search}
                                onChange={(e) => setUsersParams(p => ({ ...p, search: e.target.value, page: 1 }))}
                                className="border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
                            />
                            <select
                                value={usersParams.status}
                                onChange={(e) => setUsersParams(p => ({ ...p, status: e.target.value as any, page: 1 }))}
                                className="border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
                            >
                                <option value="">Все статусы</option>
                                <option value="active">Активные</option>
                                <option value="deleted">Удалённые</option>
                            </select>
                            <button
                                onClick={() => setUsersParams({ page: 1, per_page: 20, search: "", status: "" })}
                                className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded hover:bg-gray-300 dark:hover:bg-gray-500 text-sm"
                            >
                                Сбросить
                            </button>
                        </div>

                        {usersLoading ? (
                            <div className="text-center py-10">
                                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                            </div>
                        ) : !usersData?.data?.length ? (
                            <div className="text-center py-10 text-gray-500 dark:text-gray-400">Участники не найдены</div>
                        ) : (
                            <>
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead className="bg-gray-50 dark:bg-gray-700">
                                            <tr>
                                                {["Пользователь", "ID", "Автоплатеж", "Автосписание", "Вступил", "Статус", "Действия"].map(h => (
                                                    <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                                        {h}
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                            {usersData.data.map((u) => (
                                                <tr key={u.id} className={`hover:bg-gray-50 dark:hover:bg-gray-750 ${u.deleted_at ? "opacity-60" : ""}`}>
                                                    <td className="px-4 py-3">
                                                        <div className="flex items-center gap-2">
                                                            <img
                                                                src={u.user?.photo || "/images/no_photo.png"}
                                                                alt=""
                                                                className="w-8 h-8 rounded-full object-cover"
                                                                onError={(e) => { (e.target as HTMLImageElement).src = "/images/no_photo.png"; }}
                                                            />
                                                            <div>
                                                                <div className="font-medium text-gray-900 dark:text-gray-100">
                                                                    {u.user?.name} {u.user?.surname}
                                                                </div>
                                                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                                                    {u.user?.secret_cookie}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{u.user_id}</td>
                                                    <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100">
                                                        ${u.autopay_amount}
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${
                                                            u.autopay
                                                                ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                                                                : "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400"
                                                        }`}>
                                                            {u.autopay ? "Вкл" : "Выкл"}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-3 text-gray-500 dark:text-gray-400 text-xs">
                                                        {formatDate(u.created_at)}
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${
                                                            u.deleted_at
                                                                ? "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
                                                                : "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                                                        }`}>
                                                            {u.deleted_at ? "Удалён" : "Активен"}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <div className="flex gap-2">
                                                            {u.deleted_at ? (
                                                                <button
                                                                    onClick={() => handleRestoreUser(u.user_id)}
                                                                    className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700"
                                                                >
                                                                    Восстановить
                                                                </button>
                                                            ) : (
                                                                <button
                                                                    onClick={() => handleRemoveUser(u.user_id)}
                                                                    className="px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700"
                                                                >
                                                                    Кикнуть
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                {renderPagination(
                                    usersData.current_page,
                                    usersData.last_page,
                                    (p) => setUsersParams(prev => ({ ...prev, page: p }))
                                )}
                                <div className="px-4 py-2 text-xs text-gray-400 dark:text-gray-500">
                                    Всего записей: {usersData.total}
                                </div>
                            </>
                        )}
                    </div>
                )}

                {/* Payments Tab */}
                {activeTab === "payments" && (
                    <div>
                        {/* Payments Filters */}
                        <div className="p-4 border-b border-gray-100 dark:border-gray-700 grid grid-cols-1 md:grid-cols-4 gap-3">
                            <input
                                type="number"
                                placeholder="ID пользователя"
                                value={paymentsParams.user_id || ""}
                                onChange={(e) => setPaymentsParams(p => ({
                                    ...p,
                                    user_id: e.target.value ? parseInt(e.target.value) : undefined,
                                    page: 1
                                }))}
                                className="border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
                            />
                            <select
                                value={paymentsParams.type}
                                onChange={(e) => setPaymentsParams(p => ({ ...p, type: e.target.value as any, page: 1 }))}
                                className="border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
                            >
                                <option value="">Все типы</option>
                                <option value="manual">Ручной</option>
                                <option value="auto">Авто</option>
                            </select>
                            <input
                                type="date"
                                value={paymentsParams.date_from || ""}
                                onChange={(e) => setPaymentsParams(p => ({ ...p, date_from: e.target.value, page: 1 }))}
                                className="border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
                            />
                            <input
                                type="date"
                                value={paymentsParams.date_to || ""}
                                onChange={(e) => setPaymentsParams(p => ({ ...p, date_to: e.target.value, page: 1 }))}
                                className="border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
                            />
                        </div>

                        {paymentsLoading ? (
                            <div className="text-center py-10">
                                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                            </div>
                        ) : !paymentsData?.data?.length ? (
                            <div className="text-center py-10 text-gray-500 dark:text-gray-400">Платежи не найдены</div>
                        ) : (
                            <>
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead className="bg-gray-50 dark:bg-gray-700">
                                            <tr>
                                                {["#", "Пользователь", "Сумма", "Тип", "Валюта", "Итого накоплено", "Дата"].map(h => (
                                                    <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                                        {h}
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                            {paymentsData.data.map((p) => (
                                                <tr key={p.id} className="hover:bg-gray-50 dark:hover:bg-gray-750">
                                                    <td className="px-4 py-3 text-gray-400 dark:text-gray-500 text-xs">{p.id}</td>
                                                    <td className="px-4 py-3">
                                                        <div className="flex items-center gap-2">
                                                            <img
                                                                src={p.user?.photo || "/images/no_photo.png"}
                                                                alt=""
                                                                className="w-7 h-7 rounded-full object-cover"
                                                                onError={(e) => { (e.target as HTMLImageElement).src = "/images/no_photo.png"; }}
                                                            />
                                                            <div>
                                                                <div className="font-medium text-gray-900 dark:text-gray-100 text-xs">
                                                                    {p.user?.name} {p.user?.surname}
                                                                </div>
                                                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                                                    #{p.user_id} · {p.user?.secret_cookie}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-3 font-bold text-green-600 dark:text-green-400">
                                                        +${p.amount}
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${
                                                            p.type === "auto"
                                                                ? "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                                                                : "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200"
                                                        }`}>
                                                            {p.type === "auto" ? "Авто" : "Ручной"}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{p.currency}</td>
                                                    <td className="px-4 py-3 text-gray-600 dark:text-gray-300">${p.total}</td>
                                                    <td className="px-4 py-3 text-gray-500 dark:text-gray-400 text-xs">
                                                        {formatDate(p.created_at)}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                {renderPagination(
                                    paymentsData.current_page,
                                    paymentsData.last_page,
                                    (p) => setPaymentsParams(prev => ({ ...prev, page: p }))
                                )}
                                <div className="px-4 py-2 text-xs text-gray-400 dark:text-gray-500">
                                    Всего записей: {paymentsData.total}
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default SalaryPiggyBankPage;
